import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCheckboxField, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Bookmark, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { filterOfView, parseFilter, VIEW_OPTIONS, viewOf, writeFilter } from './filters';
import { useDeleteSavedView, useSavedViews, useSaveView } from './hooks';
export function ViewPicker({ filter, onChange }) {
    const views = useSavedViews();
    const all = views.data ?? [];
    const mine = all.filter((v)=>v.mine);
    const shared = all.filter((v)=>!v.mine);
    const view = viewOf(filter, all);
    const current = view.startsWith('saved:') ? all.find((v)=>`saved:${v.id}` === view) : undefined;
    const [saving, setSaving] = useState(false);
    const remove = useDeleteSavedView();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "View",
                className: "w-auto max-w-xs",
                "data-testid": Testids.commandCenter.viewSelect,
                onChange: (e)=>{
                    const v = e.target.value;
                    if (v === 'mine' || v === 'portfolio' || v === 'week') {
                        onChange(filterOfView(v));
                        return;
                    }
                    const hit = all.find((x)=>`saved:${x.id}` === v);
                    if (hit) onChange(parseFilter(new URLSearchParams(hit.query)));
                },
                size: "sm",
                value: view,
                children: [
                    VIEW_OPTIONS.map((o)=>/*#__PURE__*/ _jsxs("option", {
                            value: o.value,
                            children: [
                                "View: ",
                                o.label
                            ]
                        }, o.value)),
                    mine.length > 0 ? /*#__PURE__*/ _jsx("optgroup", {
                        label: "Your views",
                        children: mine.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                value: `saved:${v.id}`,
                                children: [
                                    "View: ",
                                    v.name,
                                    v.shared ? ' (shared)' : ''
                                ]
                            }, v.id))
                    }) : null,
                    shared.length > 0 ? /*#__PURE__*/ _jsx("optgroup", {
                        label: "Shared with everyone",
                        children: shared.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                value: `saved:${v.id}`,
                                children: [
                                    "View: ",
                                    v.name,
                                    v.ownerName ? ` · ${v.ownerName}` : ''
                                ]
                            }, v.id))
                    }) : null,
                    view === 'custom' ? /*#__PURE__*/ _jsx("option", {
                        value: "custom",
                        children: "View: Custom filters"
                    }) : null
                ]
            }),
            view === 'custom' ? /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.commandCenter.viewSave,
                iconLeft: /*#__PURE__*/ _jsx(Bookmark, {}),
                onClick: ()=>setSaving(true),
                size: "sm",
                variant: "ghost",
                children: "Save view"
            }) : null,
            current?.mine ? /*#__PURE__*/ _jsx(SpecraButton, {
                "aria-label": `Delete the view ${current.name}`,
                "data-testid": Testids.commandCenter.viewDelete,
                iconLeft: /*#__PURE__*/ _jsx(Trash2, {}),
                isLoading: remove.isPending,
                onClick: ()=>remove.mutate(current.id, {
                        onSuccess: ()=>toast.success(`Deleted the view “${current.name}”.`),
                        onError: (e)=>toast.error(apiErrorMessage(e, 'Could not delete the view'))
                    }),
                size: "sm",
                variant: "ghost",
                children: "Delete view"
            }) : null,
            saving ? /*#__PURE__*/ _jsx(SaveViewDialog, {
                filter: filter,
                onClose: ()=>setSaving(false)
            }) : null
        ]
    });
}
function SaveViewDialog({ filter, onClose }) {
    const [name, setName] = useState('');
    const [shared, setShared] = useState(false);
    const save = useSaveView();
    const submit = ()=>save.mutate({
            name: name.trim(),
            query: writeFilter(filter).toString(),
            shared
        }, {
            onSuccess: (v)=>{
                toast.success(shared ? `Saved “${v?.name ?? name}”. Everyone in your organisation can open it.` : `Saved “${v?.name ?? name}”.`);
                onClose();
            }
        });
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        footerProps: {
            confirmLabel: 'Save view',
            confirmDisabled: name.trim() === '' || save.isPending,
            onCancel: onClose,
            onConfirm: submit,
            confirmProps: {
                'data-testid': Testids.commandCenter.viewSaveConfirm
            }
        },
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        open: true,
        size: "sm",
        testId: Testids.commandCenter.viewSaveDialog,
        title: "Save this view",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "It keeps every filter shown now.",
                    label: "Name",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        autoFocus: true,
                        "data-testid": Testids.commandCenter.viewSaveName,
                        maxLength: 120,
                        onChange: (e)=>setName(e.target.value),
                        onKeyDown: (e)=>{
                            if (e.key === 'Enter' && name.trim()) submit();
                        },
                        value: name
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                    controlProps: {
                        checked: shared,
                        onCheckedChange: (c)=>setShared(c === true),
                        'data-testid': Testids.commandCenter.viewSaveShared
                    },
                    description: "Everyone in your organisation sees it in their View list. Only you can change or delete it.",
                    label: "Share with everyone"
                }),
                save.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    children: apiErrorMessage(save.error, 'Could not save the view')
                }) : null
            ]
        })
    });
}
